import { FileDownload, ArrowUpward, ArrowDownward, UnfoldMore } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { formatDate } from "../../utils";
import { useDispatch } from "react-redux";
import { getUnlistedHoldings } from "../../redux/rm/rmSlice";
import { getRMUser } from "../../redux/user/userSlice";
import * as XLSX from "xlsx";

const TransactionReportPending = () => {
  const dispatch = useDispatch<any>();
  const userId = Number(localStorage.getItem("ID") || "0");
  const userType = Number(localStorage.getItem("userType") || "0");
  const [transactionsData, setTransactionsData] = React.useState<any>([]);
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [rms, setRms] = React.useState<any[]>([]);
  const [filterRMId, setFilterRMId] = React.useState<number>(0);
  const [filterClient, setFilterClient] = React.useState("");
  const [filterScrip, setFilterScrip] = React.useState("");
  const [filterType, setFilterType] = React.useState("");
  const [filterStartDate, setFilterStartDate] = React.useState("");
  const [filterEndDate, setFilterEndDate] = React.useState("");

  const fetchRms = useCallback(async () => {
    try {
      const response = await dispatch(getRMUser({ userType: 2, pageIndex: 1, pageSize: 1000 }));
      setRms(response?.payload?.data || []);
    } catch (error) {
      console.error("Error fetching RMs:", error);
    }
  }, [dispatch]);

  const getUnlistedHoldingData = useCallback(async () => {
    try {
      const res = await dispatch(
        getUnlistedHoldings({ rmId: userType === 4 ? 0 : userId })
      );
      if (res?.error?.message) {
        return;
      }
      setTransactionsData(res?.payload?.data?.transactions || []);
    } catch (error) {
      console.log(error, "error");
    }
  }, [dispatch, userId, userType]);

  useEffect(() => {
    getUnlistedHoldingData();
    if (userType === 4) fetchRms();
  }, [fetchRms, getUnlistedHoldingData, userType]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };
 
  const getFilteredData = () => {
    // Only show pending transactions (where payment or share transfer is incomplete)
    let data = [...transactionsData].filter(t => !t.isPaymentDone || !t.isSharesTransferred);
    if (filterRMId) {
      const selectedRM = rms.find((r) => r.id === filterRMId);
      if (selectedRM) {
        const matchName = selectedRM.firstName.trim().toLowerCase();
        data = data.filter((t) => 
          (t.rmName || "").toLowerCase().includes(matchName) || 
          (t.rmName2 || "").toLowerCase().includes(matchName) ||
          Number(t.rmId) === filterRMId || 
          Number(t.rmId2) === filterRMId
        );
      } else {
        data = data.filter((t) => Number(t.rmId) === filterRMId || Number(t.rmId2) === filterRMId);
      }
    }
    if (filterClient) data = data.filter((t) => (t.investorName || "").toLowerCase().includes(filterClient.toLowerCase()));
    if (filterScrip) data = data.filter((t) => (t.stockName || "").toLowerCase().includes(filterScrip.toLowerCase()));
    if (filterType) data = data.filter((t) => t.type?.toUpperCase() === filterType.toUpperCase());
    if (filterStartDate) data = data.filter((t) => new Date(t.createdAt) >= new Date(filterStartDate));
    if (filterEndDate) { const end = new Date(filterEndDate); end.setHours(23,59,59,999); data = data.filter((t) => new Date(t.createdAt) <= end); }
    return data;
  };

  const getSortedData = () => {
    const filtered = getFilteredData();
    if (!sortConfig) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = (a[sortConfig.key] || "").toString().toLowerCase();
      const bVal = (b[sortConfig.key] || "").toString().toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ colKey }: { colKey: string }) => {
    if (sortConfig?.key !== colKey) return <UnfoldMore fontSize="small" sx={{ opacity: 0.4, verticalAlign: "middle" }} />;
    return sortConfig.direction === "asc"
      ? <ArrowUpward fontSize="small" sx={{ verticalAlign: "middle" }} />
      : <ArrowDownward fontSize="small" sx={{ verticalAlign: "middle" }} />;
  };

  const exportToCSV = () => {
    const tableData = getSortedData().map((txn: any) => ({
      "Transaction Date": formatDate(txn.createdAt),
      "Unique ID": txn.orderId || "-",
      "Client Name": txn.investorName || "-",
      ...(userType === 4 ? { "RM Name": txn.rmName || "-" } : {}),
      "Script Name": txn.stockName || "-",
      "Type (Buy/Sell)": txn.type || "-",
      Qty: txn.quantity || "-",
      Rate: txn.buySellPrice || "-",
      Brokerage: txn.brokerage || "-",
      BName: txn.brokerName || "-",
      NetValue: txn.netAmount || "-",
      KYC: txn.kycStatus || "-",
      "Payment Recd": txn.isPaymentDone ? "Yes" : "No",
      "Share Recd": txn.isSharesTransferred ? "Yes" : "No",
      Remarks: txn.remarks || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction Status");

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Transaction_Report_${currentDate}.csv`;
    XLSX.writeFile(workbook, filename, { bookType: "csv" });
  };

  const sortedData = getSortedData();

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "20px",
      }}
    >
      <Box
        width="98%"
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" style={{ textAlign: "left" }}>
          Transaction Reports Pending
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownload />}
          onClick={exportToCSV}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Export Data
        </Button>
      </Box>

      {/* Filter Bar */}
      <Box width="98%" mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        {userType === 4 && (
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="rm-filter-label">RM</InputLabel>
            <Select
              labelId="rm-filter-label"
              value={filterRMId}
              onChange={(e) => setFilterRMId(Number(e.target.value))}
              label="RM"
            >
              <MenuItem value={0}>All RMs</MenuItem>
              {rms.map((rm: any) => (
                <MenuItem key={rm.id} value={rm.id}>{rm.firstName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="client-filter-label">Client</InputLabel>
          <Select
            labelId="client-filter-label"
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            label="Client"
          >
            <MenuItem value="">All Clients</MenuItem>
            {Array.from(new Set(transactionsData.map((t: any) => t.investorName).filter(Boolean))).sort().map((name: any) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <input
          placeholder="Filter by Script Name"
          value={filterScrip}
          onChange={(e) => setFilterScrip(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc", minWidth: 160 }}
        />
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
        />
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
        />
        <FormControl sx={{ minWidth: 130 }} size="small">
          <InputLabel id="type-filter-label">Type</InputLabel>
          <Select
            labelId="type-filter-label"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Type"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="BUY">Buy</MenuItem>
            <MenuItem value="SELL">Sell</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={() => { setFilterRMId(0); setFilterClient(""); setFilterScrip(""); setFilterType(""); setFilterStartDate(""); setFilterEndDate(""); }}>
          Clear
        </Button>
      </Box>

      <Box width="98%" mt={2}>
        <Box width="100%" mt={2}>
          {/* Header Row */}
          <Box
            sx={{ display: "flex", p: 1, bgcolor: "grey.100", fontWeight: 600 }}
          >
            <Box sx={{ flex: 0.6 }}>Transaction Date</Box>
            <Box sx={{ flex: 0.6 }}>Unique ID</Box>
            <Box
              sx={{ flex: 1, cursor: "pointer", userSelect: "none" }}
              onClick={() => handleSort("investorName")}
            >
              Client Name <SortIcon colKey="investorName" />
            </Box>
            {userType === 4 && (
              <Box
                sx={{ flex: 1, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort("rmName")}
              >
                RM Name <SortIcon colKey="rmName" />
              </Box>
            )}
            <Box
              sx={{ flex: 2, cursor: "pointer", userSelect: "none" }}
              onClick={() => handleSort("stockName")}
            >
              Script Name <SortIcon colKey="stockName" />
            </Box>
            <Box sx={{ flex: 0.6 }}>Type (Buy/Sell)</Box>
            <Box sx={{ flex: 0.6 }}>Qty</Box>
            <Box sx={{ flex: 0.6 }}>Rate</Box>
            <Box sx={{ flex: 1 }}>Brokerage</Box>
            <Box sx={{ flex: 1 }}>B.Name</Box>
            <Box sx={{ flex: 1 }}>Net Value</Box>
            <Box sx={{ flex: 0.6 }}>KYC</Box>
            <Box sx={{ flex: 0.6 }}>Payment Recd</Box>
            <Box sx={{ flex: 0.6 }}>Share Recd</Box>
            <Box sx={{ flex: 1 }}>Remarks</Box>
          </Box>

          {/* Data Rows */}
          {sortedData.map((txn: any, index: any) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ flex: 0.6, marginLeft: "10px" }}>
                {formatDate(txn.createdAt)}
              </Box>
              <Box sx={{ flex: 0.6 }}>{txn.orderId || "-"}</Box>
              <Box sx={{ flex: 1 }}>{txn.investorName || "-"}</Box>
              {userType === 4 && (
                <Box sx={{ flex: 1 }}>{txn.rmName || "-"}</Box>
              )}
              <Box sx={{ flex: 2 }}>{txn.stockName || "-"}</Box>
              <Box sx={{ flex: 0.6 }}>{txn.type || "-"}</Box>
              <Box sx={{ flex: 0.6 }}>{txn.quantity || "-"}</Box>
              <Box sx={{ flex: 0.6 }}>{txn.buySellPrice || "-"}</Box>
              <Box sx={{ flex: 1 }}>{txn.brokerage || "-"}</Box>
              <Box sx={{ flex: 1 }}>{txn.brokerName || "-"}</Box>
              <Box sx={{ flex: 1 }}>₹{(txn.netAmount).toFixed(2)}</Box>
              <Box sx={{ flex: 0.6 }}>{txn.kycStatus || "-"}</Box>
              <Box sx={{ flex: 0.6 }}>{txn.isPaymentDone ? "Yes" : "No"}</Box>
              <Box sx={{ flex: 0.6 }}>
                {txn.isSharesTransferred ? "Yes" : "No"}
              </Box>
              <Box sx={{ flex: 1 }}>{txn.remarks || "-"}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </div>
  );
};

export default TransactionReportPending;
